import { useState } from "react";
import { ChevronDown, SlidersHorizontal, X, Search } from "lucide-react";
import { Button } from "@/components/v2/ui/button";
import { Input } from "@/components/v2/ui/input";
import { Checkbox } from "@/components/v2/ui/checkbox";
import { Slider } from "@/components/v2/ui/slider";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/v2/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/v2/ui/dropdown-menu";
import FilterDrawer from "./FilterDrawer";

const DURATIONS = ["< 2 hrs", "2-4 hrs", "Full Day"];

const SORT_OPTIONS = [
  { value: "popular", label: "Popular" },
  { value: "price-asc", label: "Price: Low → High" },
  { value: "price-desc", label: "Price: High → Low" },
  { value: "name-asc", label: "Name: A → Z" },
];

const OmSymbol = ({ className = "" }) => (
  <span className={`text-[hsl(var(--warm-gold))] opacity-60 font-serif transition-all duration-300 group-hover:text-white group-hover:opacity-100 ${className}`}>ॐ</span>
);

const FilterBar = ({
  categories = [],
  selectedCategories,
  onCategoryToggle,
  priceRange,
  onPriceChange,
  selectedDurations,
  onDurationToggle,
  sortBy,
  onSortChange,
  search,
  onSearchChange,
  onClearAll,
}) => {
  const hasPriceFilter = priceRange[0] > 3500 || priceRange[1] < 21000;
  const activeFilterCount =
    selectedCategories.length +
    selectedDurations.length +
    (hasPriceFilter ? 1 : 0);

  const currentSort = SORT_OPTIONS.find((s) => s.value === sortBy) || SORT_OPTIONS[0];

  return (
    <div className="border-b border-primary/10">
      {/* Desktop filter bar */}
      <div className="hidden md:flex items-center gap-2 py-3">
        {/* Categories popover */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={`group gap-1.5 rounded-full border-primary/30 text-sm ${selectedCategories.length > 0
                ? "bg-primary/10 border-primary text-primary"
                : "text-foreground hover:border-primary/50"
                }`}
            >
              <OmSymbol className="text-xs" />
              Categories
              {selectedCategories.length > 0 && (
                <span className="bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {selectedCategories.length}
                </span>
              )}
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-3">
            <p className="spiritual-header text-sm font-semibold mb-2 pb-2 flex items-center gap-1.5">
              <OmSymbol className="text-base" />
              Categories
            </p>
            <hr className="mandala-divider mb-2" />
            <div className="space-y-2">
              {categories.map((cat) => (
                <label key={cat.slug} className="flex items-center gap-2.5 cursor-pointer group hover:pl-1 transition-all">
                  <Checkbox
                    checked={selectedCategories.includes(cat.slug)}
                    onCheckedChange={() => onCategoryToggle(cat.slug)}
                  />
                  <span className="text-sm text-muted-foreground group-hover:text-primary transition-colors">
                    {cat.subCategoryName}
                  </span>
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Price Range popover */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={`group gap-1.5 rounded-full border-primary/30 text-sm ${hasPriceFilter
                ? "bg-primary/10 border-primary text-primary"
                : "text-foreground hover:border-primary/50"
                }`}
            >
              <OmSymbol className="text-xs" />
              Price Range
              {hasPriceFilter && (
                <span className="bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  1
                </span>
              )}
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 p-4">
            <p className="spiritual-header text-sm font-semibold mb-3 pb-2 flex items-center gap-1.5">
              <OmSymbol className="text-base " />
              Price Range
            </p>
            <hr className="mandala-divider mb-3" />
            <div className="spiritual-slider">
              <Slider
                min={3500}
                max={21000}
                step={500}
                value={priceRange}
                onValueChange={(v) => onPriceChange(v)}
                className="mb-3"
              />
            </div>
            <div className="flex justify-between items-center text-[10px] font-bold text-muted-foreground/80 uppercase tracking-tight mt-1">
              <div className="flex flex-col">
                <span className="opacity-60">Min</span>
                <span className="text-foreground">₹{priceRange[0].toLocaleString("en-IN")}</span>
              </div>
              <div className="flex flex-col text-right">
                <span className="opacity-60">Max</span>
                <span className="text-foreground">₹{priceRange[1].toLocaleString("en-IN")}</span>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Duration popover
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={`gap-1.5 rounded-full border-primary/30 text-sm ${selectedDurations.length > 0
                  ? "bg-primary/10 border-primary text-primary"
                  : "text-foreground hover:border-primary/50"
                }`}
            >
              Duration
              {selectedDurations.length > 0 && (
                <span className="bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {selectedDurations.length}
                </span>
              )}
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56 p-3">
            <p className="spiritual-header text-sm font-semibold mb-2 pb-2 flex items-center gap-1.5">
              <OmSymbol className="text-base" />
              Duration
            </p>
            <hr className="mandala-divider mb-2" />
            <div className="flex flex-wrap gap-2">
              {DURATIONS.map((dur) => (
                <button
                  key={dur}
                  onClick={() => onDurationToggle(dur)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selectedDurations.includes(dur)
                      ? "bg-primary text-primary-foreground border-primary shadow-[0_0_6px_hsl(var(--warm-gold)/0.3)]"
                      : "bg-card text-muted-foreground border-border hover:border-primary/40"
                    }`}
                >
                  {dur}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover> */}


        {/* Spacer */}
        <div className="flex-1" />

        {/* Sort dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5 rounded-full border-primary/30 text-sm">
              Sort: {currentSort.label}
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {SORT_OPTIONS.map((opt) => (
              <DropdownMenuItem
                key={opt.value}
                onClick={() => onSortChange(opt.value)}
                className={sortBy === opt.value ? "text-primary font-medium" : ""}
              >
                {sortBy === opt.value && <span className="mr-1">✓</span>}
                {opt.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Mobile filter bar */}
      <div className="flex flex-col md:hidden gap-2 py-2.5">
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          <FilterDrawer
            categories={categories}
            selectedCategories={selectedCategories}
            onCategoryToggle={onCategoryToggle}
            priceRange={priceRange}
            onPriceChange={onPriceChange}
            selectedDurations={selectedDurations}
            onDurationToggle={onDurationToggle}
            onClearAll={onClearAll}
            activeFilterCount={activeFilterCount}
          />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 rounded-full border-primary/30 text-sm shrink-0">
                Sort: {currentSort.label}
                <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {SORT_OPTIONS.map((opt) => (
                <DropdownMenuItem
                  key={opt.value}
                  onClick={() => onSortChange(opt.value)}
                  className={sortBy === opt.value ? "text-primary font-medium" : ""}
                >
                  {sortBy === opt.value && <span className="mr-1">✓</span>}
                  {opt.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
